import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { useAssignKpiToSubprojects } from '@/features/projects/project-details/hooks/use-assign-kpi-to-subprojects';
import { useAssignedSubprojectsForKpi } from '@/features/projects/project-details/hooks/use-assigned-subprojects-for-kpi';
import { useProjectSubprojects } from '@/features/projects/shared/hooks/use-project-subprojects';
import { Button } from '@/shared/components/ui/button';
import { Checkbox } from '@/shared/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { LabelInput } from '@/shared/components/ui/label-input';
import { getKpiUnitTranslationPlural } from '@/shared/hooks/use-kpi-unit-translation';
import { toast } from '@/shared/hooks/use-toast';
import { Project } from '@/shared/types/api';
import {
  getDatabaseErrorKey,
  logDatabaseError,
} from '@/shared/utils/database-errors';
import {
  formatNumberWithDots,
  parseFormattedNumber,
} from '@/shared/utils/format';

interface KpiAssignmentDialogProps {
  kpiId: number;
  kpiName: string;
  kpiUnit: string | null;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

export const KpiAssignmentDialog = ({
  kpiId,
  kpiName,
  kpiUnit,
  isOpen,
  onOpenChange,
}: KpiAssignmentDialogProps) => {
  const { t } = useTranslation('kpi', { keyPrefix: 'kpi_assignment_dialog' });
  const { t: tCommon } = useTranslation('common');
  const { t: tKpiTypes } = useTranslation('kpiTypes', { nsMode: 'fallback' });
  const { projectId } = useParams();
  const kpiNameWithUnit = `${kpiName} ${getKpiUnitTranslationPlural(tKpiTypes, kpiUnit)}`;

  const { data: subprojects, isLoading: isSubprojectsLoading } =
    useProjectSubprojects({
      projectId: Number(projectId),
    });

  const {
    data: assignedSubprojectIds = [],
    isLoading: isAssignedSubprojectsLoading,
  } = useAssignedSubprojectsForKpi(kpiId);

  const isLoading = isSubprojectsLoading || isAssignedSubprojectsLoading;

  const [subprojectIds, setSubprojectIds] = useState<
    { id: number; budget: number | undefined; isSelected: boolean }[]
  >([]);

  const { mutate: assignKpi, isPending } = useAssignKpiToSubprojects(
    Number(projectId),
  );

  const availableSubprojects = subprojects?.filter(
    (subproject) => !assignedSubprojectIds.includes(subproject.id),
  );

  useEffect(() => {
    if (availableSubprojects) {
      setSubprojectIds(
        availableSubprojects.map((subproject) => ({
          id: subproject.id,
          budget: undefined,
          isSelected: false,
        })),
      );
    }
  }, [availableSubprojects]);

  const handleAssign = () => {
    const selectedSubprojectIds = subprojectIds.filter(
      (subproject) => subproject.isSelected,
    );
    if (!selectedSubprojectIds.length) return;

    assignKpi(
      {
        kpiId,
        subprojects: selectedSubprojectIds.map((subproject) => ({
          clientViewId: subproject.id,
          budget: subproject.budget,
        })),
      },
      {
        onSuccess: () => {
          toast({
            title: t('success_message'),
          });
          onOpenChange(false);
          setSubprojectIds((prev) =>
            prev.map((subproject) => ({
              ...subproject,
              isSelected: false,
            })),
          );
        },
        onError: (error) => {
          logDatabaseError(error);
          const errorKey = getDatabaseErrorKey(error);
          const errorMessage = errorKey
            ? tCommon(errorKey as any)
            : t('error_message');
          toast({
            title: errorMessage,
            variant: 'destructive',
          });
        },
      },
    );
  };

  const isAssignDisabled =
    !subprojectIds.filter((subproject) => subproject.isSelected).length ||
    isPending;

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent closeInline>
        <DialogHeader>
          <DialogTitle className="text-3xl">{t('title')}</DialogTitle>
          <p className="text-lg text-muted-foreground">{kpiNameWithUnit}</p>
        </DialogHeader>

        <div className="mt-10 flex flex-col gap-12">
          <div className="flex flex-col gap-12">
            {isLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 3 }).map((_, index) => (
                  <div
                    key={index}
                    className="flex animate-pulse items-center space-x-3 rounded-lg bg-gray-50 p-3"
                  >
                    <div className="h-5 w-5 rounded-full bg-gray-300" />
                    <div className="h-4 w-32 rounded bg-gray-300" />
                    <div className="ml-auto h-8 w-20 rounded bg-gray-300" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-12">
                {availableSubprojects?.map((subproject: Project) => {
                  const subprojectState = subprojectIds.find(
                    (sp) => sp.id === subproject.id,
                  );
                  return (
                    <div
                      key={subproject.id}
                      className="mt-4 flex items-center justify-between gap-4"
                    >
                      <div className="flex flex-1 items-center space-x-3">
                        <Checkbox
                          checked={subprojectState?.isSelected ?? false}
                          onCheckedChange={(checked) => {
                            setSubprojectIds((prev) =>
                              prev.map((sp) =>
                                sp.id === subproject.id
                                  ? { ...sp, isSelected: !!checked }
                                  : sp,
                              ),
                            );
                          }}
                        />
                        <span className="font-medium">{subproject.name}</span>
                      </div>
                      <div className="flex flex-1 items-center">
                        <LabelInput
                          label={t('budget_label')}
                          value={
                            formatNumberWithDots(subprojectState?.budget) ?? ''
                          }
                          onChange={(e) => {
                            const budget = parseFormattedNumber(e.target.value);
                            setSubprojectIds((prev) =>
                              prev.map((sp) =>
                                sp.id === subproject.id
                                  ? { ...sp, budget }
                                  : sp,
                              ),
                            );
                          }}
                          placeholder="0"
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="flex justify-center">
            <Button
              onClick={handleAssign}
              disabled={isAssignDisabled}
              variant="greenGradient"
              btnType="primary"
              className="w-40"
            >
              {isPending ? t('assigning') : t('assign_button')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
